import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
import HeaderBar from '@root/components/HeaderBarNew';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import useSortData from '@root/hooks/useSortData';
import { TitleTab } from '@root/components/v2/Tab';
// import { VULNERABILITY_SCANS } from './vulnerabilityScanData';
import { vulnerabilityScanColumns } from './vulnerabilityScanColumns';
import VulnerabilityScanSideOverlay from './VulnerabilityScanSideOverlay';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import agent from '@root/agent';
import { capitalizeFirstLetter } from '@root/utilities/string';
import FilterComponent from '@root/components/FilterNew';

const STATUS_FILTERS = ['all', 'errors', 'completed'];

const VulnerabilityScansTab = ({ environments = [], enabled = false }) => {
  const [searchString, setSearchString] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const { sortData, handleSortModelChange } = useSortData();
  const [openFilter, setOpenFilter] = useState('');
  const debouncedSearchString = useDebouncedValue(searchString, 400);

  const environmentIds = useMemo(() => environments.map(e => e.id), [environments]);
  const [selectedFilters, setSelectedFilters] = useState({
    clusters: [],
    regions: [],
  });

  const {
    data: vulnListResponse,
    isLoading,
    isFetching,
  } = useEnhancedQuery(
    [
      'get_k8s_vuln_scan_history_list',
      `${environmentIds}`,
      currentPage,
      pageSize,
      debouncedSearchString,
      sortData,
      statusFilter,
      selectedFilters,
    ],
    () =>
      agent.Kubernetes.get_k8s_vuln_scan_history_list({
        environments: environmentIds,
        clusters: selectedFilters.clusters,
        regions: selectedFilters.regions,
        status: statusFilter === 'all' ? '' : statusFilter,
        search_query: debouncedSearchString,
        page_number: currentPage,
        rows_per_page: pageSize,
        export: false,
        sort: sortData?.length
          ? sortData.map(({ key, value }) => ({ field: key, order: value }))
          : [{ field: 'scan_timestamp', order: 'desc' }],
      }),
    {
      enabled,
      staleTime: 30000,
      cacheTime: 300000,
      keepPreviousData: true,
    },
  );

  const { data: vulnListFilterKeys, isFetching: isFetchingFilterKeys } = useEnhancedQuery(
    ['get_k8s_vuln_scan_history_filter', `${environmentIds}`],
    () =>
      agent.Kubernetes.get_k8s_vuln_scan_history_filter({
        environments: environmentIds,
        search_query: '',
      }),
    {
      enabled,
      staleTime: 30000,
      cacheTime: 300000,
      keepPreviousData: true,
    },
  );

  const tableRows = useMemo(() => {
    const items = vulnListResponse?.data ?? [];
    return items.map((item, idx) => ({
      ...item,
      id: item.scan_id ?? `scan-${idx}`,
    }));
  }, [vulnListResponse]);

  const propertyValues = useMemo(() => {
    if (isFetchingFilterKeys) return [];
    const filterData = vulnListFilterKeys?.data ?? {};
    return Object.fromEntries(
      Object.entries(filterData)
        .filter(([key, property]) => key !== 'statuses' && property?.num_records > 0)
        .map(([key, property]) => [
          // Transform key
          key
            ?.split('_')
            ?.map(word => capitalizeFirstLetter(word))
            ?.join(' '),

          // Transform array values
          property?.data ?? [],
        ]),
    );
  }, [isFetchingFilterKeys, vulnListFilterKeys?.data]);

  const isFilterDataFetching = useMemo(() => {
    return isFetchingFilterKeys;
  }, [isFetchingFilterKeys]);

  const isFilterDisabled = useMemo(() => {
    if (isFetching || isFetchingFilterKeys) return true;
    return (
      (!vulnListResponse?.pagination?.total || vulnListResponse?.pagination?.total === 0) &&
      !Object.values(selectedFilters).some(arr => arr.length > 0)
    );
  }, [isFetching, isFetchingFilterKeys, vulnListResponse?.pagination?.total, selectedFilters]);

  const tableColumns = useMemo(() => {
    return vulnerabilityScanColumns;
  }, []);

  const handleSearchChange = useCallback(e => {
    setSearchString(e.target.value);
  }, []);

  const handlePageChange = useCallback(
    page => {
      if (isFetching) return;
      setCurrentPage(page);
    },
    [isFetching],
  );

  const handlePageSizeChange = useCallback(_pageSize => {
    setCurrentPage(1);
    setPageSize(_pageSize);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchString]);

  // const counts = useMemo(
  //   () => ({
  //     all: vulnListResponse?.pagination?.total,
  //     errors: tableRows.filter(scan => scan.scan_status === 'errors').length,
  //     completed: tableRows.filter(scan => scan.scan_status === 'completed').length,
  //   }),
  //   [tableRows, vulnListResponse?.pagination?.total],
  // );

  const tabItems = useMemo(
    () => [
      {
        label: (
          <TabLabel
            text="All"
            // count={counts.all}
          />
        ),
        value: 'all',
      },
      {
        label: (
          <TabLabel
            text="With Errors"
            // count={counts.errors}
          />
        ),
        value: 'with_errors',
      },
      {
        label: (
          <TabLabel
            text="Completed"
            // count={counts.completed}
          />
        ),
        value: 'completed',
      },
    ],
    [],
  );

  const handleRowClick = useCallback(r => {
    setSelectedRow(r.row?.original || r.row || r.original || r);
    setShowDetailsPanel(true);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchString, statusFilter]);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <TitleTab
        tabs={statusFilter}
        setTabs={setStatusFilter}
        items={tabItems}
        validTabs={STATUS_FILTERS}
        enableUrlState={false}
        containerClasses={{ marginBottom: '15px' }}
      />
      <HeaderBar
        searchBar
        searchBarValue={searchString}
        setSearchTerm={setSearchString}
        searchBarOnChange={handleSearchChange}
        onSearchBarSubmit={e => e.preventDefault()}
        marginBottom={'15px'}
        exportButton={false}
        setOpenFilter={setOpenFilter}
        openFilter={openFilter}
        isFilterDataFetching={isFilterDataFetching}
        isFilterDisabled={isFilterDisabled}
      />
      <FilterComponent
        openFilter={openFilter}
        option={{ ...propertyValues }}
        setSelectedFilters={setSelectedFilters}
        selectedFilters={selectedFilters}
        defaultValue={{}}
      />
      <MuiTable
        rows={tableRows}
        columns={tableColumns}
        LoaderState={isLoading || isFetching}
        page={currentPage}
        total_size={vulnListResponse?.pagination?.total ?? 0}
        pageSize={pageSize}
        rowHeight={56}
        isDynamicHeight={true}
        paginationMode={'server'}
        handleSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onRowClick={handleRowClick}
        disableDynamicColumn={true}
        noDataMessage="No vulnerability scans available"
      />
      {showDetailsPanel && (
        <VulnerabilityScanSideOverlay
          open={showDetailsPanel}
          onClose={() => setShowDetailsPanel(false)}
          selectedRow={selectedRow}
        />
      )}
    </Box>
  );
};

const TabLabel = ({
  text,
  // count
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
    {text}
    {/* <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '20px',
        height: '20px',
        padding: '0 6px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        backgroundColor: 'var(--action-bar-bg, #f3f4f6)',
        color: 'inherit',
      }}>
      {count}
    </span> */}
  </span>
);

export default VulnerabilityScansTab;
